/**
 * The required reason, and the sentence that says what it is for.
 *
 * ONE COMPONENT FOR BOTH FORMS, because the copy is the point. A tenant edit and
 * a credit top-up ask for the same thing for the same reason, and two
 * hand-written versions of that explanation would drift into two different
 * standards for what counts as an explanation — which is the same as no
 * standard.
 *
 * WHY THE LABEL IS NOT "REASON". A field labelled "Reason", required, with no
 * explanation, is filled in with the word "correction" — and then the audit row
 * that was supposed to survive staff turnover says "correction". The label here
 * names the *question the field will be asked to answer*, months later, by
 * somebody who was not in the room: why is this workspace's balance what it is,
 * why did this subscription move, why was this workspace suspended. A person who
 * can see the question writes an answer to it.
 *
 * WHY THE MINIMUM IS SHOWN BEFORE IT IS ENFORCED. `adminReasonSchema` trims and
 * then requires eight characters, which is the shortest run that can hold a word
 * and a number ("topup 5k", "typo fix"). Discovering that as a validation error
 * after pressing Save teaches people to type eight characters, not to explain;
 * saying it under the box while they type asks for the sentence up front. The
 * counter appears only once the cap is in sight, because a character count
 * hovering over an empty box reads as a length target.
 *
 * THE VALUE IS NOT TRIMMED ON THE WAY THROUGH. `.trim()` runs inside the
 * contract, so a body of spaces is refused rather than stored as a reason of
 * length zero; trimming here as well would make the control fight the person
 * typing a space between two words.
 *
 * THE CAP IS A PROP, AND IT HAD TO BECOME ONE. It was `MAX_ADMIN_REASON_LENGTH`
 * — 500 — hard-coded, on both forms. That is right for the tenant edit, whose
 * reason lands in `admin_audit.reason` (`CHECK … BETWEEN 8 AND 500`), and wrong
 * for the top-up, whose reason lands in `audit_log.metadata` and is refused
 * above 256. The two forms sit on one screen in the same visual state, so a
 * 300-character explanation counted itself up to "300 / 500" in the ordinary
 * colour on the top-up form, passed the client's own parse, passed the two-step
 * review, and came back as a 400 — while the identical text saved without
 * complaint on the form directly above it. One component giving both forms one
 * standard was the right idea; stating a limit that is false on one of them was
 * not. The caller passes the cap its own contract enforces, so the number under
 * the box and the number the API applies are the same number.
 */
import { MIN_ADMIN_REASON_LENGTH } from '@wend/shared';
import { FormField } from '@wend/ui';

import { Textarea } from '../../components/Textarea';

/**
 * Show the counter once the value is within this much of the cap.
 *
 * A margin rather than a fraction: the point is "you are approaching the limit",
 * and that is a fixed number of remaining characters whether the limit is 256 or
 * 500. A percentage would put the counter on screen 100 characters earlier on
 * the longer form for no reason the person typing could see.
 */
const COUNTER_VISIBLE_WITHIN = 100;

export interface ReasonFieldProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  /**
   * What this particular reason will be asked to explain, in one clause.
   * Rendered inside a fixed sentence, so it reads as "…so that six months from
   * now, <clause>."
   */
  explains: string;
  /**
   * The cap this form's own contract enforces — `MAX_ADMIN_REASON_LENGTH` for a
   * tenant edit, `MAX_TOPUP_REASON_LENGTH` for a top-up. Required rather than
   * defaulted: a default is how one of the two callers silently gets the other's
   * limit, which is the defect this prop exists to close.
   */
  maxLength: number;
  disabled?: boolean;
}

export function ReasonField({
  value,
  onChange,
  error,
  explains,
  maxLength,
  disabled = false,
}: ReasonFieldProps) {
  const length = value.trim().length;
  const tooShort = length > 0 && length < MIN_ADMIN_REASON_LENGTH;
  const showCounter = value.length >= maxLength - COUNTER_VISIBLE_WITHIN;

  return (
    <FormField
      label="Why are you making this change?"
      required
      error={error}
      description={
        <>
          This is recorded permanently against your staff account and cannot be edited afterwards.
          Write it for somebody who was not here: {explains}
        </>
      }
    >
      <div className="flex flex-col gap-1.5">
        <Textarea
          rows={3}
          value={value}
          disabled={disabled}
          maxLength={maxLength}
          placeholder="Invoice INV-2291 paid on 4 Sep — 5,000 email credits agreed with the customer"
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />

        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          {tooShort ? (
            <p className="text-[0.78rem] leading-snug text-muted">
              A few more words — at least {MIN_ADMIN_REASON_LENGTH} characters.
            </p>
          ) : (
            <span />
          )}

          {showCounter && (
            <p className="font-mono text-[0.7rem] tabular-nums tracking-[0.06em] text-muted">
              {value.length} / {maxLength}
            </p>
          )}
        </div>
      </div>
    </FormField>
  );
}
